import { Task, TaskType } from "../types/v2/Task";
import { Action } from "../types/v2/Automation";
import { DUE_DATE_UNITS, DueDateUnit, resolveDueDate } from "./taskDueDate";

export type CreateTaskActionValue = {
  title: string;
  description?: string;
  assigneeId?: string;
  type: TaskType;
  dueDate?: { amount: number; unit: DueDateUnit };
};

export const DEFAULT_CREATE_TASK_ACTION_VALUE: CreateTaskActionValue = {
  title: "",
  type: "general",
};

export function encodeCreateTaskActionValue(
  value: CreateTaskActionValue
): string {
  return JSON.stringify(value);
}

function isTaskType(v: unknown): v is TaskType {
  return v === "general" || v === "update" || v === "parts" || v === "other";
}

function isValidDueDate(
  v: unknown
): v is { amount: number; unit: DueDateUnit } {
  if (typeof v !== "object" || v === null) return false;
  const d = v as { amount?: unknown; unit?: unknown };
  return (
    typeof d.amount === "number" &&
    Number.isInteger(d.amount) &&
    d.amount >= 0 &&
    (DUE_DATE_UNITS as readonly string[]).includes(d.unit as string)
  );
}

export function parseCreateTaskActionValue(
  raw: string | number | null | undefined
): CreateTaskActionValue {
  if (typeof raw !== "string" || raw.trim() === "") {
    return { ...DEFAULT_CREATE_TASK_ACTION_VALUE };
  }
  try {
    const parsed = JSON.parse(raw) as Partial<CreateTaskActionValue>;
    const value: CreateTaskActionValue = {
      title: typeof parsed.title === "string" ? parsed.title : "",
      type: isTaskType(parsed.type) ? parsed.type : "general",
    };
    if (
      typeof parsed.description === "string" &&
      parsed.description.length > 0
    ) {
      value.description = parsed.description;
    }
    if (typeof parsed.assigneeId === "string" && parsed.assigneeId.length > 0) {
      value.assigneeId = parsed.assigneeId;
    }
    if (isValidDueDate(parsed.dueDate)) {
      value.dueDate = parsed.dueDate;
    }
    return value;
  } catch {
    return { ...DEFAULT_CREATE_TASK_ACTION_VALUE };
  }
}

export function buildTaskInput(
  value: CreateTaskActionValue,
  cardId: string,
  now: Date = new Date()
): Partial<Task> {
  const input: Partial<Task> = {
    title: value.title,
    type: value.type,
    relatedRO: cardId,
    completed: false,
  };
  if (value.description) input.description = value.description;
  if (value.assigneeId) input.assigneeId = value.assigneeId;
  if (value.dueDate) {
    input.dueDate = resolveDueDate({
      mode: "relative",
      amount: value.dueDate.amount,
      unit: value.dueDate.unit,
      from: now,
    });
  }
  return input;
}

export function createTaskInputsFromActions(
  actions: Action[],
  cardId: string,
  now: Date = new Date()
): Partial<Task>[] {
  return actions
    .filter((a) => a.actionField === "createTask")
    .map((a) =>
      buildTaskInput(parseCreateTaskActionValue(a.actionValue), cardId, now)
    );
}
